import { Link } from 'react-router-dom';
import { PageHero, CTABanner } from '../../components/PageHero';
import SEO from '../../components/SEO';
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHalved, faFileLines, faLayerGroup, faGift,
  faCode, faLink, faDatabase, faPalette,
  faUsers, faChartBar, faLock, faBolt,
  faCheckCircle, faEnvelope,
} from "@fortawesome/free-solid-svg-icons";

const API_URL = process.env.REACT_APP_API_URL || 'https://mannaplag.mannaanalytics.com/api';

interface Plan {
  plan_id: string;
  pricing_id: string;
  name: string;
  price: number;
  discounted_amount: number;
  currency_symbol: string;
  billing_cycle: string;
  max_papers: number;
  max_users: number;
}

/* ── Shared pricing section ──────────────────────────────── */
function PricingSection({ type, title }: { type: string; title: string }) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/plans/?type=${type}&billing_cycle=Annual`)
      .then(r => r.json())
      .then(data => { if (data.plans) setPlans(data.plans); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [type]);

  if (loading) return null;
  if (!plans.length) return null;

  return (
    <section className="bg-white border-b border-gray-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <h2 className="text-[28px] font-black text-gray-900 mb-3">{title}</h2>
          <p className="text-[14px] text-gray-500">Annual billing · Cancel anytime</p>
        </div>
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${Math.min(plans.length, 4)} gap-6`}>
          {plans.map((plan: Plan) => {
            const isFree = plan.price === 0;
            const isPro = plan.name === 'Pro';
            const featureList = [
              { show: true, label: isFree ? '1 check per month' : `${plan.max_papers} checks per year` },
              { show: true, label: plan.max_users === 1 ? '1 user' : `Up to ${plan.max_users} users` },
              { show: (plan as any).ai_detection, label: 'AI content detection' },
              { show: (plan as any).full_report, label: 'Full similarity report' },
              { show: (plan as any).rest_api, label: 'REST API access' },
              { show: (plan as any).webhook, label: 'Webhook notifications' },
              { show: (plan as any).priority_processing, label: 'Priority processing' },
              { show: (plan as any).dedicated_manager, label: 'Dedicated account manager' },
            ].filter(f => f.show);

            return (
              <div key={plan.plan_id} className={`p-6 rounded-xl relative transition-colors ${
                isPro ? 'border-2 border-navy shadow-md' : 'border border-gray-100 hover:border-gray-200'
              }`}>
                {isPro && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-navy text-white text-[10px] px-3 py-1 rounded-full whitespace-nowrap font-bold">
                    Most Popular
                  </div>
                )}
                <p className="text-[11px] font-bold text-blue-600 uppercase tracking-wide mb-2">{plan.name}</p>
                {isFree ? (
                  <p className="text-[30px] font-black text-gray-900 mb-1">Free</p>
                ) : (
                  <>
                    <p className="text-[30px] font-black text-gray-900 mb-0 leading-none">
                      {plan.currency_symbol}{plan.discounted_amount.toLocaleString('en-IN')}
                    </p>
                    <p className="text-[11px] text-gray-400 mb-1">
                      + GST {plan.currency_symbol}{(plan as any).gst_amount?.toLocaleString('en-IN') ?? ''}
                    </p>
                  </>
                )}
                <p className="text-[12px] text-gray-400 mb-5">{isFree ? 'Forever free' : plan.billing_cycle}</p>

                <div className="space-y-2 mb-6">
                  {featureList.map((f, i) => (
                    <p key={i} className="text-[12px] text-gray-600 flex items-center gap-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-[10px] flex-shrink-0" />
                      {f.label}
                    </p>
                  ))}
                </div>

                <Link
                   to={(plan as any).contact_sales ? '/contact?inquiry=enterprise' : '/register'}
                  state={(plan as any).contact_sales ? undefined : { role: type === 'Institution' ? 'institution' : type === 'Company' ? 'publisher' : 'researcher' }}
                  className={`block text-center py-2.5 rounded-lg text-[12px] font-bold transition-colors ${
                    isPro
                      ? 'bg-navy text-white hover:opacity-90'
                      : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {isFree ? 'Get Started Free' : (plan as any).contact_sales ? 'Contact Sales' : 'Choose Plan'}
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── Custom plan CTA ─────────────────────────────────────── */
function CustomPlanCTA() {
  return (
    <section className="bg-gray-50 border-b border-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
        <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-5">
          <FontAwesomeIcon icon={faEnvelope} className="text-blue-600 text-lg" />
        </div>
        <h2 className="text-[22px] font-black text-gray-900 mb-3">Need a customised plan?</h2>
        <p className="text-[14px] text-gray-500 max-w-xl mx-auto mb-6">
          If your submission volume, team size, or workflow requirements go beyond our standard plans,
          we'll build a package that fits. Custom pricing, dedicated support, and API access are available.
        </p>
        <Link
          to="/contact?inquiry=enterprise"
          className="inline-block bg-navy text-white text-[13px] font-bold px-6 py-3 rounded-xl hover:opacity-90 transition-opacity"
        >
          Talk to us about a custom plan
        </Link>
      </div>
    </section>
  );
}

/* ── FOR RESEARCHERS ─────────────────────────────────────── */
export function ForResearchers() {
  const features = [
    {
      icon: faGift,
      title: "Start for Free",
      desc: "Get 3 free plagiarism and AI content detection checks with no credit card required. See the quality of your reports before you commit.",
    },
    {
      icon: faFileLines,
      title: "Comprehensive Similarity Reports",
      desc: "Receive detailed plagiarism reports with source-by-source matching, percentage breakdown, highlighted text, and a downloadable PDF report.",
    },
    {
      icon: faShieldHalved,
      title: "AI Content Detection",
      desc: "Identify AI-generated content in your manuscript using our ModernBERT-powered detector trained on ChatGPT, Claude, and GPT-4 output.",
    },
    {
      icon: faLayerGroup,
      title: "Check Before Submission",
      desc: "Use Manna Plag at every stage. from choosing your research title to final manuscript submission. to ensure originality throughout.",
    },
  ];

  return (
    <div>
      <SEO
        title="Plagiarism & AI Checker for Research Papers & Theses"
        description="Check academic manuscripts, theses, and dissertations for similarity and AI content before journal submission. Confidential checking with no repository retention."
        canonicalPath="/researchers"
        keywords="plagiarism checker for research papers, thesis plagiarism check, check dissertation for plagiarism, turnitin without repository, pre-submission plagiarism check, academic ai detector"
      />
      <PageHero
        eyebrow="For Researchers"
        title="Know where your manuscript stands before the editor does"
        subtitle="Manna Plag gives researchers a complete plagiarism and AI content detection report before journal submission. Identify similarity, paraphrasing, self-plagiarism, and AI-generated content in minutes. so your work is ready when it matters most."
        ctas={[
          { label: 'Get started free', to: '/register', primary: true },
          { label: 'View pricing', to: '/pricing' },
        ]}
      />

      <section className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <p className="text-[14px] text-gray-600 leading-relaxed mb-5">
            Academic publishing is more competitive than ever. Journals and conferences use sophisticated plagiarism detection software. and a single similarity flag can delay or reject months of research. Manna Plag puts the same detection power in your hands before you submit, giving you the chance to correct issues on your own terms.
          </p>
          <p className="text-[14px] text-gray-600 leading-relaxed">
            From the moment you choose your research title, Manna Plag helps you verify originality. Check whether your title has been used before, scan draft sections as you write, and run a comprehensive final check before submission. Our AI detection engine also flags content that reads as machine-generated. increasingly important as journals tighten their AI usage policies. Get a similarity score, section-level breakdown, and a professional PDF report you can share with co-authors or supervisors.
          </p>
        </div>
      </section>

      <section className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-center mb-10">
            <h2 className="text-[28px] font-black text-gray-900 mb-3">Everything researchers need</h2>
            <p className="text-[14px] text-gray-500 max-w-2xl mx-auto">
              Plagiarism detection, AI content checking, and detailed similarity reports. all in one platform built for academic researchers.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map(f => (
              <div key={f.title} className="bg-white border border-gray-100 rounded-xl p-6 hover:border-gray-200 transition-colors">
                <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center mb-4">
                  <FontAwesomeIcon icon={f.icon} className="text-blue-600 text-lg" />
                </div>
                <p className="text-[14px] font-bold text-gray-900 mb-2">{f.title}</p>
                <p className="text-[12px] text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PricingSection type="Individual" title="Plans for individual researchers" />
      <CustomPlanCTA />

      <CTABanner
        title="Start checking your manuscript for free"
        subtitle="3 free checks. No credit card required. Similarity report delivered in minutes."
        primaryLabel="Get started free"
        primaryTo="/register"
        secondaryLabel="View all plans"
        secondaryTo="/pricing"
      />
    </div>
  );
}

/* ── FOR INSTITUTIONS ────────────────────────────────────── */
export function ForInstitutions() {
  const features = [
    {
      icon: faUsers,
      title: "Multi-User Team Management",
      desc: "Add faculty, reviewers, and administrators to a shared account. Manage roles, track usage, and control access from a single dashboard.",
    },
    {
      icon: faChartBar,
      title: "Submission History & Analytics",
      desc: "Track every submission across your institution. View trends, flag repeat violations, and generate reports for compliance and accreditation.",
    },
    {
      icon: faFileLines,
      title: "Bulk Manuscript Checking",
      desc: "Upload and check multiple manuscripts simultaneously. Ideal for thesis evaluation seasons, journal intake, and semester-end submissions.",
    },
    {
      icon: faShieldHalved,
      title: "AI Content Detection",
      desc: "Detect AI-generated content in student and faculty submissions using our classifier trained on ChatGPT, Claude, DeepSeek, and GPT-4.",
    },
    {
      icon: faLock,
      title: "Data Privacy & Security",
      desc: "Submitted manuscripts are stored securely and never shared with third parties. Compliant with institutional data governance requirements.",
    },
    {
      icon: faBolt,
      title: "Fast, Accurate Results",
      desc: "Receive comprehensive plagiarism and AI detection reports within minutes. Section-level breakdown, source matching, and PDF download included.",
    },
  ];

  return (
    <div>
      <SEO
        title="Academic Integrity Software for Universities & Colleges"
        description="Scalable plagiarism detection and AI content screening for higher education institutions. Multi-user accounts, LMS integration, and bulk thesis evaluation."
        canonicalPath="/institutions"
        keywords="academic integrity software for universities, institutional plagiarism checker, campus plagiarism detection, bulk thesis plagiarism checker, higher education plagiarism tool"
      />
      <PageHero
        eyebrow="For Institutions"
        title="Scalable academic integrity software for universities and colleges"
        subtitle="Manna Plag gives universities, colleges, and research institutions the tools to enforce academic integrity at scale. with multi-user team management, bulk manuscript checking, AI content detection, and detailed similarity reports built for institutional workflows."
        ctas={[
          { label: 'Get started free', to: '/register', primary: true },
          { label: 'View pricing', to: '/pricing' },
        ]}
      />

      <section className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <p className="text-[14px] text-gray-600 leading-relaxed mb-5">
            Academic institutions face growing pressure to maintain research integrity across faculty publications, postgraduate theses, and student submissions. With AI-generated content becoming indistinguishable from human writing, institutions need detection tools that go beyond traditional similarity checking. Manna Plag combines plagiarism detection with a purpose-built AI content classifier to give your institution a complete picture of every submission.
          </p>
          <p className="text-[14px] text-gray-600 leading-relaxed">
            Our institutional plans are designed for teams. not just individuals. Add multiple reviewers and administrators to a shared account, assign roles, track submission history, and generate compliance reports. Whether you're running a journal, processing thesis submissions, or managing peer review across departments, Manna Plag fits into your existing workflow without disruption.
          </p>
        </div>
      </section>

      <section className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-center mb-10">
            <h2 className="text-[28px] font-black text-gray-900 mb-3">Built for institutional workflows</h2>
            <p className="text-[14px] text-gray-500 max-w-2xl mx-auto">
              From thesis screening to faculty publication checks. everything your institution needs to uphold academic integrity standards.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map(f => (
              <div key={f.title} className="bg-white border border-gray-100 rounded-xl p-6 hover:border-gray-200 transition-colors">
                <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center mb-4">
                  <FontAwesomeIcon icon={f.icon} className="text-blue-600 text-lg" />
                </div>
                <p className="text-[14px] font-bold text-gray-900 mb-2">{f.title}</p>
                <p className="text-[12px] text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PricingSection type="Institution" title="Plans for universities and colleges" />
      <CustomPlanCTA />

      <CTABanner
        title="Set up academic integrity checking for your institution"
        subtitle="Multi-user accounts, bulk checking, and AI detection. all in one platform."
        primaryLabel="Get started free"
        primaryTo="/register"
        secondaryLabel="Talk to us"
        secondaryTo="/contact?inquiry=enterprise"
      />
    </div>
  );
}

/* ── FOR PUBLISHERS ──────────────────────────────────────── */
export function ForPublishers() {
  const features = [
    {
      icon: faCode,
      title: "REST API Integration",
      desc: "Integrate Manna Plag directly into your editorial management system via our REST API. Automate submission screening without manual uploads.",
    },
    {
      icon: faLink,
      title: "Webhook Notifications",
      desc: "Receive real-time webhook callbacks when reports are ready. Build automated editorial workflows that act on results instantly.",
    },
    {
      icon: faDatabase,
      title: "High-Volume Processing",
      desc: "Handle thousands of manuscript submissions per month. Our infrastructure scales with your journal's submission volume without degradation.",
    },
    {
      icon: faShieldHalved,
      title: "AI Content Detection",
      desc: "Flag AI-generated manuscripts before peer review begins. Our ModernBERT classifier detects ChatGPT, Claude, DeepSeek, and paraphrased AI content.",
    },
    {
      icon: faFileLines,
      title: "Detailed Similarity Reports",
      desc: "Source-by-source matching with percentage scores, highlighted passages, and PDF reports. formatted for editorial decision-making.",
    },
    {
      icon: faPalette,
      title: "API Key Management",
      desc: "Issue and manage API keys per team member or integration. Monitor usage, set limits, and revoke access from a centralised dashboard.",
    },
  ];

  const apiExample = `POST /api/checker/analyze/
Authorization: Api-Key MAPL_your_key_here
Content-Type: multipart/form-data

file: manuscript.pdf
title: "Your manuscript title"

→ 202 Accepted
{
  "submission_id": "abc-123",
  "status": "processing"
}

GET /api/submissions/abc-123/
→ 200 OK
{
  "status": "complete",
  "overall_similarity": 12,
  "overall_ai_score": 4,
  "report_id": "abc-123"
}`;

  return (
    <div>
      <SEO
        title="Journal Manuscript Screening & iThenticate Alternative"
        description="Automate plagiarism and AI content screening across journal submissions. REST API, webhook callbacks, OJS integration, and high-volume peer-review workflows."
        canonicalPath="/publishers"
        keywords="ithenticate alternative for journals, journal manuscript screening software, academic publisher plagiarism api, ojs plagiarism plugin, peer review plagiarism check"
      />
      <PageHero
        eyebrow="For Publishers"
        title="Automate manuscript integrity screening for academic journals"
        subtitle="Manna Plag gives academic publishers and journal editors a REST API and editorial dashboard to automate plagiarism detection and AI content screening across every manuscript submission. at any volume."
        ctas={[
          { label: 'Get started free', to: '/register', primary: true },
          { label: 'View API docs', to: '/api-reference' },
        ]}
      />

      <section className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <p className="text-[14px] text-gray-600 leading-relaxed mb-5">
            Journal editors receive hundreds of manuscripts every month. Manually screening each one for plagiarism and AI-generated content is time-consuming and inconsistent. Manna Plag automates this step. integrating directly into your editorial workflow via REST API, so every manuscript is screened the moment it arrives, before peer review begins.
          </p>
          <p className="text-[14px] text-gray-600 leading-relaxed">
            Our publisher plans include full API access, webhook support, team management for editorial staff, and high-volume processing capacity. Whether you run a single journal or a portfolio of publications, Manna Plag scales with your submission volume. AI content detection is included on every plan. so you can enforce your journal's AI usage policy from day one without additional tools or costs.
          </p>
        </div>
      </section>

      <section className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-center mb-10">
            <h2 className="text-[28px] font-black text-gray-900 mb-3">Everything publishers need</h2>
            <p className="text-[14px] text-gray-500 max-w-2xl mx-auto">
              API integration, webhook callbacks, AI detection, and high-volume screening. built for academic journal editorial workflows.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map(f => (
              <div key={f.title} className="bg-white border border-gray-100 rounded-xl p-6 hover:border-gray-200 transition-colors">
                <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center mb-4">
                  <FontAwesomeIcon icon={f.icon} className="text-blue-600 text-lg" />
                </div>
                <p className="text-[14px] font-bold text-gray-900 mb-2">{f.title}</p>
                <p className="text-[12px] text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-gray-100" id="api">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-8">
            <h2 className="text-[28px] font-black text-gray-900 mb-3">Simple REST API</h2>
            <p className="text-[14px] text-gray-500 max-w-xl mx-auto">
              Submit manuscripts and retrieve reports programmatically. Full documentation and API key management included.
            </p>
          </div>
          <div className="bg-navy rounded-xl p-6 overflow-x-auto max-w-3xl mx-auto">
            <pre className="text-[12px] text-green-400 font-mono leading-relaxed">{apiExample}</pre>
          </div>
          <div className="text-center mt-6">
            <Link to="/api-reference" className="text-[13px] font-bold text-blue-600 hover:underline">
              View full API documentation →
            </Link>
          </div>
        </div>
      </section>

      <PricingSection type="Company" title="Plans for publishers and journals" />
      <CustomPlanCTA />

      <CTABanner
        title="Integrate plagiarism screening into your editorial workflow"
        subtitle="REST API, webhook support, AI detection, and team management. all on one platform."
        primaryLabel="Get started free"
        primaryTo="/register"
        secondaryLabel="View API docs"
        secondaryTo="/api-reference"
      />
    </div>
  );
}

/* ── FOR CORPORATE ───────────────────────────────────────── */
export function ForCorporate() {
  const features = [
    {
      title: 'Bulk Content Checks',
      desc: 'Check multiple documents simultaneously. reports, proposals, marketing copy, and internal communications. without checking word by word.',
    },
    {
      title: 'AI Content Detection',
      desc: 'Verify whether content submitted by teams or contractors was generated by AI tools like ChatGPT or Claude before it goes out under your brand.',
    },
    {
      title: 'Fits Your Workflow',
      desc: 'Deploy Manna Plag across your organisation via our web dashboard or REST API without disrupting how your teams already work.',
    },
    {
      title: 'Eliminate Copyright Risk',
      desc: 'Catch reused or unoriginal content before publication, reducing exposure to copyright disputes and protecting your organisation\'s reputation.',
    },
  ];

  return (
    <div>
      <SEO
        title="Enterprise Content Integrity & AI Copyright Protection"
        description="Verify content originality and AI writing across marketing copy, corporate documentation, proposals, and internal communications. Bulk screening via API."
        canonicalPath="/corporate"
        keywords="corporate plagiarism checker, enterprise content integrity, ai copywriting check, copyright risk detection, business plagiarism protection"
      />
      <PageHero
        eyebrow="For Corporate"
        title="Protect your organisation's content integrity at scale"
        subtitle="As AI-generated content becomes harder to detect and copyright risks increase, organisations need reliable tools to verify the originality of every document. Manna Plag gives your teams bulk plagiarism checking, AI content detection, and detailed reports across all your content operations."
        ctas={[
          { label: 'Talk to us', to: '/contact?inquiry=enterprise', primary: true },
          { label: 'View pricing', to: '/pricing' },
        ]}
      />

      <section className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <p className="text-[14px] text-gray-600 leading-relaxed mb-5">
            Most organisations depend on written content for their documentation, proposals, public relations, marketing, and internal and external communication. As teams increasingly use AI writing tools, ensuring that content is original, accurate, and free of copyright exposure becomes a compliance requirement. not just a quality concern.
          </p>
          <p className="text-[14px] text-gray-600 leading-relaxed">
            Manna Plag fits into your workflow and lets you manage all your content in bulk, instead of checking document by document. Our API allows integration with existing content management systems, and our team dashboard gives managers visibility across all submissions.
          </p>
        </div>
      </section>

      <section className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {features.map(f => (
              <div key={f.title} className="bg-white border border-gray-100 rounded-xl p-6 hover:border-gray-200 transition-colors">
                <p className="text-[14px] font-bold text-gray-900 mb-2">{f.title}</p>
                <p className="text-[13px] text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CustomPlanCTA />

      <CTABanner
        title="Protect your organisation's content integrity"
        subtitle="Talk to us about deploying Manna Plag across your teams and workflows."
        primaryLabel="Get in touch"
        primaryTo="/contact?inquiry=enterprise"
      />
    </div>
  );
}

/* ── FOR INDIVIDUALS ─────────────────────────────────────── */
export function ForIndividuals() {
  const features = [
    {
      title: 'Instant Plagiarism Check',
      desc: 'Upload your document and receive a detailed similarity report within minutes. with source-by-source matching and percentage scores.',
    },
    {
      title: 'AI Content Detection',
      desc: 'Check whether your content contains AI-generated text before submitting to journals, conferences, or academic institutions.',
    },
    {
      title: 'Check Before Your Reviewer Does',
      desc: 'Get the same plagiarism detection your journal or institution uses. before you submit. Fix issues on your own terms, not under pressure.',
    },
    {
      title: 'Unintentional Plagiarism, Caught',
      desc: 'Even well-researched work can contain unintentional similarities. Manna Plag flags them early so you can cite correctly and submit with confidence.',
    },
  ];

  return (
    <div>
      <SEO
        title="Plagiarism Checker & AI Detector for Students & Writers"
        description="Affordable plagiarism and AI content detection for students, writers, and freelance researchers. Instant similarity reports without university login requirements."
        canonicalPath="/individuals"
        keywords="student plagiarism checker, turnitin alternative for students, cheap plagiarism checker, essay originality check, ai detector for students"
      />
      <PageHero
        eyebrow="For Individuals"
        title="Check your work for plagiarism before your reviewer does"
        subtitle="Manna Plag gives individuals. writers, students, and independent researchers. access to the same plagiarism detection and AI content checking tools used by universities and publishers. Get a complete similarity report in minutes, with no institutional account required."
        ctas={[
          { label: 'Get started free', to: '/register', primary: true },
          { label: 'View pricing', to: '/pricing' },
        ]}
      />

      <section className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {features.map(f => (
              <div key={f.title} className="bg-white border border-gray-100 rounded-xl p-6 hover:border-gray-200 transition-colors">
                <p className="text-[14px] font-bold text-gray-900 mb-2">{f.title}</p>
                <p className="text-[13px] text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PricingSection type="Individual" title="Simple plans for individuals" />
      <CustomPlanCTA />

      <CTABanner
        title="Start checking for free today"
        subtitle="3 free checks. No credit card required. Results in minutes."
        primaryLabel="Get started free"
        primaryTo="/register"
        secondaryLabel="View all plans"
        secondaryTo="/pricing"
      />
    </div>
  );
}
